import { defineOnce, OsviChat, OsviButtons, OsviCarousel, OsviDatePicker } from '../ui/index';
import { createChatClient, DEFAULT_BASE_URL, type ChatClient, type ChatState } from '../core';
import { HttpClient, type Bootstrap } from '../core/http';
import { ChatRequestError } from '../core/errors';
import { browserStore, memoryStore, getVisitorId, sessionHintKey } from '../core/storage';
import { compareCursors, interactionCursor } from '../core/protocol/readers';
import { applyTheme } from '../ui/theme';
import { readConfig, type WidgetConfig } from './config';
import { OsviChatWidget } from './launcher';

export interface WidgetApi { open(): void; close(): void; toggle(): void; destroy(): void; init(config?: Partial<WidgetConfig>): void }
export const GREETING_KEY = (agentId: string): string => `osvi.chat.greetingDismissed.${agentId}`;
export const SEEN_KEY = (agentId: string): string => `osvi.chat.seen.${agentId}`;
export const BADGE_RECHECK_MS = 30_000;
export const PROBE_TIMEOUT_MS = 8_000;
let activeApi: WidgetApi | null = null;
// A hung page-load probe must not leave the launcher hidden; abort is handled as a network failure (fail open).
function probeSignal(): { signal: AbortSignal; cancel(): void } {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') return { signal: AbortSignal.timeout(PROBE_TIMEOUT_MS), cancel() {} };
  const controller = new AbortController(), timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  return { signal: controller.signal, cancel: () => clearTimeout(timer) };
}
export function unmountWidget(): void { activeApi?.destroy(); }
export function mountWidget(initial: WidgetConfig): WidgetApi {
  // Runtime references keep registrations in bundles even when source modules
  // are considered side-effect free by the package's bundler metadata.
  defineOnce('osvi-chat', OsviChat);
  defineOnce('osvi-buttons', OsviButtons);
  defineOnce('osvi-carousel', OsviCarousel);
  defineOnce('osvi-date-picker', OsviDatePicker);
  defineOnce('osvi-chat-widget', OsviChatWidget);
  if (activeApi) return activeApi;
  let config = readConfig(null, initial), host: OsviChatWidget | null = null, client: ChatClient | null = null;
  let unsubscribe: (() => void) | undefined, opened = false, everOpened = false, lastCheck = -Infinity, generation = 0, checkSequence = 0;
  let previousOverflow: string | null = null;
  let disabled = false, probed: Bootstrap['agent'] | null = null;
  const anonymousStore = browserStore();
  let store = memoryStore();
  let known = new Set<string>(), unread = new Set<string>();
  const keys = (state: ChatState): Set<string> => new Set([
    ...state.items.flatMap(i => i.kind === 'message' && i.role !== 'user' ? [`message:${i.serverId ?? i.id}`] : []),
    ...(state.activeInteraction ? [`interaction:${state.activeInteraction.interaction_id}`] : []),
  ]);
  const updateState = (state: ChatState): void => {
    if (!host) return;
    const next = keys(state);
    if (opened) {
      let maximum = interactionCursor(store.get(SEEN_KEY(config.agentId)));
      for (const item of state.items) {
        const id = interactionCursor(item.kind === 'message' && item.role !== 'user' ? item.serverId : item.kind === 'interaction' ? item.interaction.id : undefined);
        if (id !== null && (maximum === null || compareCursors(id, maximum) > 0)) maximum = id;
      }
      const seen = interactionCursor(store.get(SEEN_KEY(config.agentId)));
      if (maximum !== null && (seen === null || compareCursors(maximum, seen) > 0)) store.set(SEEN_KEY(config.agentId), maximum);
      unread.clear(); host.badge = 0;
    } else {
      const seen = interactionCursor(store.get(SEEN_KEY(config.agentId)));
      const eligible = new Set(state.items.flatMap(item => {
        if (item.kind !== 'message' || item.role === 'user') return [];
        const id = interactionCursor(item.serverId);
        return id !== null && seen !== null && compareCursors(id, seen) <= 0 ? [] : [`message:${item.serverId ?? item.id}`];
      }));
      if (state.activeInteraction) {
        const id = interactionCursor(state.activeInteraction.id);
        if (id === null || seen === null || compareCursors(id, seen) > 0) eligible.add(`interaction:${state.activeInteraction.interaction_id}`);
      }
      for (const key of next) if (!known.has(key) && eligible.has(key)) unread.add(key);
      host.badge = unread.size;
    }
    known = next;
  };
  // One anonymous bootstrap serves as the page-load probe (is Public chat on?) and, for a returning
  // visitor, the unread check. A first-time visitor sends no visitor id, so nothing is created or stored.
  const checkUnread = async (): Promise<void> => {
    if (config.auth?.mode === 'server' || !host) return;
    const returning = !!store.get(sessionHintKey(config.agentId));
    if (everOpened && (probed || !returning)) return;
    lastCheck = Date.now(); const current = generation, sequence = ++checkSequence;
    const live = (): boolean => !!host && generation === current && sequence === checkSequence;
    const probe = probeSignal();
    try {
      const boot = await new HttpClient(config.baseUrl ?? DEFAULT_BASE_URL).bootstrap(config.agentId, returning ? getVisitorId(store) : undefined, probe.signal);
      if (!live()) return;
      probed = boot.agent ?? null; disabled = false; host.available = true;
      if (!returning || everOpened) return;
      const seen = interactionCursor(store.get(SEEN_KEY(config.agentId)));
      host.badge = seen === null ? 0 : (boot.messages ?? []).filter(row => {
        if (!row || typeof row !== 'object') return false;
        const r = row as Record<string, unknown>, id = interactionCursor(r.id);
        return r.role !== 'user' && r.role !== 'system' && id !== null && compareCursors(id, seen) > 0;
      }).length;
    } catch (e) {
      if (!live()) return;
      if (e instanceof ChatRequestError && e.error.code === 'public_chat_disabled') {
        if (!disabled) console.warn('[osvi-chat] Anonymous usage is turned off for this agent, so the chat widget is hidden. Turn it on in the agent\'s Channels → Web widget settings and press Save.');
        disabled = true; host.available = false; if (opened) api.close(); return;
      }
      disabled = false; host.available = true;   // a network or server failure (or a probe timeout) must not hide the chat
      if (returning && !everOpened) host.badge = 0;
    } finally { probe.cancel(); }
  };
  const visibility = (): void => { if (document.visibilityState === 'visible' && Date.now() - lastCheck >= BADGE_RECHECK_MS) void checkUnread(); };
  const resize = (): void => {
    if (opened && window.innerWidth < 640) { if (previousOverflow === null) previousOverflow = document.documentElement.style.overflow; document.documentElement.style.overflow = 'hidden'; }
    else if (previousOverflow !== null) { document.documentElement.style.overflow = previousOverflow; previousOverflow = null; }
  };
  const escape = (event: KeyboardEvent): void => { if (event.key === 'Escape' && opened) { api.close(); host?.focusLauncher(); } };
  const api: WidgetApi = {
    open() {
      if (!host || opened || disabled) return;
      opened = true; everOpened = true; host.available = true; host.opened = true; host.badge = 0; unread.clear(); resize();
      if (!client) {
        client = createChatClient({ agentId: config.agentId, baseUrl: config.baseUrl, auth: config.auth, store, initialAgent: probed ?? undefined });
        host.client = client; known = keys(client.getState()); unsubscribe = client.subscribe(updateState);
        void client.start().catch(() => {});
      }
      updateState(client.getState());
      const target = host; void target.updateComplete.then(() => { if (host === target && opened) void target.panel?.focusActiveInteraction(); });
    },
    close() { if (!host || !opened) return; opened = false; host.opened = false; resize(); host.focusLauncher(); },
    toggle() { if (opened) api.close(); else api.open(); },
    destroy() {
      api.close(); ++generation; unsubscribe?.(); unsubscribe = undefined; client?.destroy(); client = null;
      host?.remove(); host = null; document.removeEventListener('visibilitychange', visibility); document.removeEventListener('keydown', escape); window.removeEventListener('resize', resize);
      delete (window as Window & { __osviChatWidget?: boolean }).__osviChatWidget;
      if (activeApi === api) activeApi = null;
    },
    init(next = {}) {
      if (host) return;
      if (activeApi && activeApi !== api) return;
      config = readConfig(null, { ...config, ...next });
      if (!config.agentId) { console.error('[osvi-chat] data-agent is required'); return; }
      store = config.auth?.mode === 'server' ? memoryStore() : anonymousStore;
      opened = false; everOpened = false; disabled = false; probed = null; lastCheck = -Infinity; known = new Set(); unread = new Set(); ++generation;
      host = document.createElement('osvi-chat-widget') as OsviChatWidget; host.config = config; host.available = config.auth?.mode === 'server';
      host.setAttribute('position', config.position ?? 'right'); applyTheme(host, config.theme);
      host.greetingDismissed = !!store.get(GREETING_KEY(config.agentId)); host.onToggle = api.toggle; host.onClose = api.close;
      host.onDismiss = () => { if (host) { host.greetingDismissed = true; store.set(GREETING_KEY(config.agentId), 'true'); } };
      document.body.append(host); activeApi = api; (window as Window & { __osviChatWidget?: boolean }).__osviChatWidget = true;
      document.addEventListener('visibilitychange', visibility); document.addEventListener('keydown', escape); window.addEventListener('resize', resize); void checkUnread();
    },
  };
  api.init(); return api;
}
